import { useMemo } from 'react';
import { Box, Stack, TextField, Typography, Alert, Chip } from '@mui/material';
import { MyLocation } from '@mui/icons-material';
import ZoneMapPanel from '../maps/ZoneMapPanel';

const HUB_TYPES = new Set(['airports', 'railway_stations', 'bus_stations']);

interface HubCoordinatePickerProps {
  masterType: string;
  lat?: number;
  lng?: number;
  onChange: (lat: number, lng: number) => void;
}

export function isTransportHubType(type: string) {
  return HUB_TYPES.has(type);
}

export default function HubCoordinatePicker({ masterType, lat, lng, onChange }: HubCoordinatePickerProps) {
  const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
  const center = useMemo(
    () => (hasCoords ? { lat: lat!, lng: lng! } : undefined),
    [hasCoords, lat, lng],
  );

  if (!isTransportHubType(masterType)) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <MyLocation fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>Map coordinates</Typography>
        {hasCoords && <Chip size="small" label={`${lat!.toFixed(5)}, ${lng!.toFixed(5)}`} color="success" variant="outlined" />}
      </Stack>
      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        Click the map to set the hub pickup point. Coordinates are used dynamically in booking search and fare zone detection.
      </Alert>
      <ZoneMapPanel
        height={220}
        center={center}
        zoom={hasCoords ? 14 : 11}
        pin={hasCoords ? { lat: lat!, lng: lng! } : null}
        onMapClick={(clickLat, clickLng) => onChange(clickLat, clickLng)}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          label="Latitude"
          type="number"
          value={lat ?? ''}
          onChange={(e) => onChange(Number(e.target.value), lng ?? center?.lng ?? 0)}
          inputProps={{ step: 0.00001 }}
          fullWidth
        />
        <TextField
          size="small"
          label="Longitude"
          type="number"
          value={lng ?? ''}
          onChange={(e) => onChange(lat ?? center?.lat ?? 0, Number(e.target.value))}
          inputProps={{ step: 0.00001 }}
          fullWidth
        />
      </Stack>
    </Box>
  );
}
